import { apiFetch, readApiErrorMessage } from '../utils/apiClient';
import { resolveMemberCounts } from './utils/memberFilter';
import type {
  LscApiErrorBody,
  LscArchiveAutomationPolicy,
  LscArchiveAutopublishSettings,
  LscArchiveAutopublishEligibility,
  LscArchiveAutopublishDryRunPreview,
  LscArchiveAutopublishKillSwitch,
  LscLiveAutomationPolicy,
  LscArchiveItem,
  LscDetectionPayload,
  LscDetectionSwitchValue,
  LscCsvImportDetails,
  LscCsvImportIssue,
  LscCsvImportResult,
  LscNeonsportzUrlPreviewResult,
  LscNeonsportzPublishedSeason,
  LscImportedMemberPromotionResult,
  LscLeagueInviteCreateResult,
  LscLeagueInviteListResult,
  LscLeagueInvitePreviewResult,
  LscJoinRequestAcceptResult,
  LscJoinRequestDecisionResult,
  LscJoinRequestListResult,
  LscMyJoinRequestListResult,
  LscMemberClaimDecisionResult,
  LscMemberClaimListResult,
  LscMemberClaimSubmitResult,
  LscMemberClaimSuggestionsResult,
  LscMyClaimOpportunitiesResult,
  LscNeonsportzUrlConfirmResult,
  LscLeague,
  LscLeagueDetail,
  LscLiveDashboardResponse,
  LscLiveDashboardRow,
  LscLiveLeagueGame,
  LscLiveTruthStatusResponse,
  RunLscLiveTruthCheckPayload,
  RunLscLiveTruthCheckResponse,
  ConfirmLscBroadcasterSidePayload,
  ConfirmLscBroadcasterSideResponse,
  LscMatch,
  LscMember,
  LscMemberInviteBulkRow,
  LscMemberInviteClaimResult,
  LscMemberInviteIssueResult,
  LscMemberInvitePreview,
  LscMemberInviteSummary,
  LscMembersListResult,
  LscMemberTeamOptionsResult,
  LscMemberProviderSyncResult,
  LscPublishResponse,
  LscIgnoreVodCandidatePayload,
  LscIgnoreVodCandidateResponse,
  LscReviewAction,
  LscNeedsAttentionAction,
  LscNeedsAttentionItem,
  LscNeedsAttentionMatchOptions,
  LscNeedsAttentionReassignResult,
  LscReviewQueueItem,
  LscReviewResponse,
  LscScheduleSource,
  LscScheduleSourceStatus,
  LscScheduleSourceSyncResult,
  LscScheduleStaleRepairResult,
  ScheduleRepairApplyRequest,
  ScheduleRepairApplyResponse,
  ScheduleRepairPreviewRequest,
  ScheduleRepairPreviewResponse,
  ScheduleRepairStatus,
  LscSyncRunSummary,
  LscAutoSyncRun,
  LscSyncStatus,
  LscLiveReadinessReport,
  LscLiveSubscription,
  LscLiveSubscriptionSyncRequest,
  LscLiveSubscriptionSyncResult,
  LscVodSyncReadiness,
  LscAutoVerifyEligibilityReport,
  LscVodCandidate,
  LscVodHandoffSession,
  LscVodMatchResult,
  LscVodMatchSuggestion,
  LscCommissionerDigest,
  LscPaidBetaSupportStatus,
  LscBillingStatusResponse,
  LscCheckoutSessionResponse,
  LscBillingPortalSessionResponse,
  LscBillingCadence,
  LscBindLeagueResponse,
  LscCommissionerAccessResponse,
  LscLeagueReadiness,
  LscLeagueSetupProgress,
  LscLeagueSetupProgressStepId,
  LscLeagueAutomationAccess,
  LscAutomationActivationPayload,
  LscSupportIssue,
  LscSupportIssueCreateRequest,
  LscSupportIssueCreateResponse,
  LscSupportIssueMineResponse,
  LscAdminSupportIssue,
  LscAdminIssueListFilters,
  LscAdminIssueListResponse,
  LscAdminIssueDetailResponse,
  LscAdminIssueUpdateRequest,
  LscAdminIssueUpdateResponse,
  LscAdminLeagueSummary,
  LscAdminLeagueSummaryResponse,
  LscAdminHealth,
  LscAdminHealthResponse,
} from './types';
import type {
  CfbAliasPayload,
  CfbAssignmentPayload,
  CfbDynastyAssignment,
  CfbDynastySeason,
  CfbIdentityAlias,
  CfbSeasonCreatePayload,
  CfbSetupCompleteness,
} from './cfbSetupTypes';

export class LscApiError extends Error {
  status: number;
  code?: string;
  details?: LscCsvImportDetails;
  issues?: LscCsvImportIssue[] | string[];

  constructor(
    message: string,
    status: number,
    code?: string,
    extras?: {
      details?: LscCsvImportDetails;
      issues?: LscCsvImportIssue[] | string[];
    }
  ) {
    super(message);
    this.name = 'LscApiError';
    this.status = status;
    this.code = code;
    this.details = extras?.details;
    this.issues = extras?.issues;
  }
}

export class LscPublishConflictError extends LscApiError {
  archiveId?: string;

  constructor(message: string, archiveId?: string) {
    super(message, 409, 'conflict');
    this.name = 'LscPublishConflictError';
    this.archiveId = archiveId;
  }
}

function leaguePath(leagueId: string, suffix: string): string {
  return `/api/lsc/leagues/${encodeURIComponent(leagueId)}${suffix}`;
}

/** Founder preview. The server ignores this unless the caller is the founder. */
export function withMemberPreview(path: string): string {
  if (typeof window === 'undefined') return path;
  const onPage = new URLSearchParams(window.location.search).get('as') === 'member';
  if (!onPage) return path;
  const [pathname, query = ''] = path.split('?');
  const search = new URLSearchParams(query);
  search.set('preview_role', 'member');
  const qs = search.toString();
  return qs ? `${pathname}?${qs}` : pathname;
}

async function lscRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await apiFetch(path, init);
  let body: (T & LscApiErrorBody) | LscApiErrorBody | null = null;

  try {
    body = (await response.json()) as T & LscApiErrorBody;
  } catch {
    body = null;
  }

  if (!response.ok) {
    const message =
      (body && typeof body.message === 'string' && body.message) ||
      (body && typeof body.error === 'string' && body.error) ||
      (await readApiErrorMessage(response)) ||
      `Request failed (${response.status})`;
    const code = body && typeof body.error === 'string' ? body.error : undefined;
    const details =
      body && typeof body === 'object' && 'details' in body
        ? (body.details as LscCsvImportDetails | undefined)
        : undefined;
    const issues =
      body && Array.isArray(body.errors)
        ? (body.errors as LscCsvImportIssue[] | string[])
        : undefined;
    throw new LscApiError(message, response.status, code, { details, issues });
  }

  return body as T;
}

async function lscRequestRaw(
  path: string,
  init?: RequestInit
): Promise<{ response: Response; body: LscApiErrorBody | null }> {
  const response = await apiFetch(path, init);
  let body: LscApiErrorBody | null = null;
  try {
    body = (await response.json()) as LscApiErrorBody;
  } catch {
    body = null;
  }
  return { response, body };
}

export interface CreateLscLeaguePayload {
  name: string;
  slug: string;
  game_key?: string;
  game_label?: string;
  visibility?: string;
  league_tag?: string;
}

export async function listLscLeagues(params?: { includeArchived?: boolean }): Promise<LscLeague[]> {
  const search = new URLSearchParams();
  if (params?.includeArchived) search.set('include_archived', 'true');
  const qs = search.toString();
  const path = withMemberPreview('/api/lsc/leagues' + (qs ? `?${qs}` : ''));
  const data = await lscRequest<{ success: boolean; leagues: LscLeague[] }>(path);
  return data.leagues ?? [];
}

export async function createLscLeague(
  payload: CreateLscLeaguePayload
): Promise<LscLeague> {
  const data = await lscRequest<{ success: boolean; league: LscLeague }>(
    '/api/lsc/leagues',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  );
  return data.league;
}

export interface UpdateLscLeaguePayload {
  name?: string;
  game_key?: string;
  game_label?: string;
  visibility?: string;
  league_tag?: string;
  streaming_obligation?: 'away' | null;
}

export async function updateLscLeague(
  leagueId: string,
  payload: UpdateLscLeaguePayload
): Promise<LscLeague> {
  const data = await lscRequest<{ success: boolean; league: LscLeague }>(
    leaguePath(leagueId, ''),
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  );
  return data.league;
}

export async function setLscStreamPages(
  leagueId: string,
  streamPages: 'off' | 'on'
): Promise<LscLeague> {
  const data = await lscRequest<{ success: boolean; league: LscLeague }>(
    leaguePath(leagueId, '/stream-pages'),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ stream_pages: streamPages }),
    }
  );
  return data.league;
}

export interface UpdateLscArchiveAutomationPolicyPayload {
  archive_automation_policy: Partial<LscArchiveAutomationPolicy>;
  change_reason?: string;
}

export async function updateLscArchiveAutomationPolicy(
  leagueId: string,
  payload: UpdateLscArchiveAutomationPolicyPayload
): Promise<LscLeague> {
  const data = await lscRequest<{ success: boolean; league: LscLeague }>(
    leaguePath(leagueId, '/archive-automation-policy'),
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  );
  return data.league;
}

export interface UpdateLscArchiveAutopublishSettingsPayload {
  archive_autopublish_settings: Partial<LscArchiveAutopublishSettings>;
  change_reason?: string;
}

export async function getLscArchiveAutopublishSettings(
  leagueId: string
): Promise<LscArchiveAutopublishSettings> {
  const data = await lscRequest<{
    success: boolean;
    archive_autopublish_settings: LscArchiveAutopublishSettings;
  }>(leaguePath(leagueId, '/archive-autopublish-settings'));
  return data.archive_autopublish_settings;
}

export async function updateLscArchiveAutopublishSettings(
  leagueId: string,
  payload: UpdateLscArchiveAutopublishSettingsPayload
): Promise<LscArchiveAutopublishSettings> {
  const data = await lscRequest<{
    success: boolean;
    archive_autopublish_settings: LscArchiveAutopublishSettings;
  }>(leaguePath(leagueId, '/archive-autopublish-settings'), {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return data.archive_autopublish_settings;
}

export async function getLscArchiveAutopublishEligibility(
  leagueId: string,
  options?: { candidateId?: string }
): Promise<LscArchiveAutopublishEligibility> {
  const query = options?.candidateId
    ? `?candidate_id=${encodeURIComponent(options.candidateId)}`
    : '';
  const data = await lscRequest<{
    success: boolean;
    archive_autopublish_eligibility: LscArchiveAutopublishEligibility;
  }>(leaguePath(leagueId, `/archive-autopublish-eligibility${query}`));
  return data.archive_autopublish_eligibility;
}

export async function getLscArchiveAutopublishDryRunPreview(
  leagueId: string,
  options?: { candidateId?: string }
): Promise<LscArchiveAutopublishDryRunPreview> {
  const query = options?.candidateId
    ? `?candidate_id=${encodeURIComponent(options.candidateId)}`
    : '';
  const data = await lscRequest<{
    success: boolean;
    archive_autopublish_dry_run_preview: LscArchiveAutopublishDryRunPreview;
  }>(leaguePath(leagueId, `/archive-autopublish-dry-run-preview${query}`));
  return data.archive_autopublish_dry_run_preview;
}

export async function getLscArchiveAutopublishSafety(
  leagueId: string,
  options?: { candidateId?: string }
): Promise<LscArchiveAutopublishKillSwitch> {
  const query = options?.candidateId
    ? `?candidate_id=${encodeURIComponent(options.candidateId)}`
    : '';
  const data = await lscRequest<{
    success: boolean;
    archive_autopublish_kill_switch: LscArchiveAutopublishKillSwitch;
    audit_schema_version?: number;
  }>(leaguePath(leagueId, `/archive-autopublish-safety${query}`));
  return data.archive_autopublish_kill_switch;
}

export interface UpdateLscLiveAutomationPolicyPayload {
  live_automation_policy: Partial<LscLiveAutomationPolicy>;
  change_reason?: string;
}

export async function updateLscLiveAutomationPolicy(
  leagueId: string,
  payload: UpdateLscLiveAutomationPolicyPayload
): Promise<LscLeague> {
  const data = await lscRequest<{ success: boolean; league: LscLeague }>(
    leaguePath(leagueId, '/live-automation-policy'),
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  );
  return data.league;
}

export async function uploadLscLeagueLogo(
  leagueId: string,
  formData: FormData
): Promise<LscLeague> {
  const data = await lscRequest<{ success: boolean; league: LscLeague }>(
    leaguePath(leagueId, '/logo'),
    {
      method: 'POST',
      body: formData,
    }
  );
  return data.league;
}

export async function deleteLscLeagueLogo(leagueId: string): Promise<LscLeague> {
  const data = await lscRequest<{ success: boolean; league: LscLeague }>(
    leaguePath(leagueId, '/logo'),
    {
      method: 'DELETE',
    }
  );
  return data.league;
}

export async function getLscLeague(leagueId: string): Promise<LscLeagueDetail> {
  const data = await lscRequest<{
    success: boolean;
    league: LscLeague;
    viewer_is_commissioner?: boolean;
    viewer_is_owner?: boolean;
    viewer_is_operator?: boolean;
    effective_role?: 'owner' | 'commissioner' | 'member';
    effective_membership?: LscMember | null;
    membership?: LscMember | null;
  }>(withMemberPreview(leaguePath(leagueId, '')));
  const resolvedMembership = data.effective_membership ?? data.membership ?? null;
  return {
    league: data.league,
    viewerIsCommissioner: Boolean(data.viewer_is_operator ?? data.viewer_is_commissioner),
    viewerIsOwner: Boolean(data.viewer_is_owner),
    viewerIsOperator: Boolean(data.viewer_is_operator ?? data.viewer_is_commissioner),
    effectiveRole: data.effective_role,
    membership: resolvedMembership,
    effectiveMembership: resolvedMembership,
  };
}

export async function archiveLscLeague(
  leagueId: string,
  payload: { confirmSlug?: string } = {}
): Promise<LscLeague> {
  const data = await lscRequest<{ success: boolean; league: LscLeague }>(
    leaguePath(leagueId, '/archive'),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(
        payload.confirmSlug ? { confirm_slug: payload.confirmSlug } : {}
      ),
    }
  );
  return data.league;
}

export async function restoreLscLeague(leagueId: string): Promise<LscLeague> {
  const data = await lscRequest<{ success: boolean; league: LscLeague }>(
    leaguePath(leagueId, '/restore'),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    }
  );
  return data.league;
}

export async function listLscMembers(leagueId: string): Promise<LscMembersListResult> {
  const data = await lscRequest<{
    success: boolean;
    members: LscMember[];
    counts?: LscMembersListResult['counts'];
  }>(leaguePath(leagueId, '/members'));
  const members = data.members ?? [];
  const counts = resolveMemberCounts(members, data.counts);
  return { members, counts };
}

export async function listLscMemberInvites(
  leagueId: string
): Promise<LscMemberInviteSummary[]> {
  const data = await lscRequest<{ success: boolean; invites: LscMemberInviteSummary[] }>(
    leaguePath(leagueId, '/member-invites')
  );
  return data.invites ?? [];
}

export async function createLscMemberInvite(
  leagueId: string,
  memberId: string
): Promise<LscMemberInviteIssueResult> {
  return lscRequest<LscMemberInviteIssueResult>(
    leaguePath(leagueId, `/members/${encodeURIComponent(memberId)}/member-invite`),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    }
  );
}

export async function bulkCreateLscMemberInvites(
  leagueId: string
): Promise<{ invites: LscMemberInviteBulkRow[]; count: number; failed: { member_id: string; display_name: string; message: string }[] }> {
  return lscRequest(
    leaguePath(leagueId, '/member-invites/bulk'),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    }
  );
}

export async function unclaimLscMember(
  leagueId: string,
  memberId: string
): Promise<{ success: boolean; member_id: string; status: string }> {
  return lscRequest(
    leaguePath(leagueId, `/members/${encodeURIComponent(memberId)}/member-invite/unclaim`),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    }
  );
}

export async function reinviteLscMember(
  leagueId: string,
  memberId: string
): Promise<LscMemberInviteIssueResult> {
  return lscRequest<LscMemberInviteIssueResult>(
    leaguePath(leagueId, `/members/${encodeURIComponent(memberId)}/member-invite/reinvite`),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    }
  );
}

export async function previewLscMemberInvite(token: string): Promise<LscMemberInvitePreview> {
  return lscRequest<LscMemberInvitePreview>(
    `/api/lsc/member-invites/${encodeURIComponent(token)}`
  );
}

export async function claimLscMemberInvite(token: string): Promise<LscMemberInviteClaimResult> {
  return lscRequest<LscMemberInviteClaimResult>(
    `/api/lsc/member-invites/${encodeURIComponent(token)}/claim`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    }
  );
}

export async function restoreLscMember(
  leagueId: string,
  memberId: string,
  teamLabel?: string
): Promise<LscMember> {
  const data = await lscRequest<{ success: boolean; member: LscMember }>(
    leaguePath(leagueId, `/members/${encodeURIComponent(memberId)}/restore`),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(teamLabel ? { current_side_label: teamLabel } : {}),
    }
  );
  return data.member;
}

export async function removeLscMember(leagueId: string, memberId: string): Promise<LscMember> {
  const data = await lscRequest<{ success: boolean; member: LscMember }>(
    leaguePath(leagueId, `/members/${encodeURIComponent(memberId)}/remove`),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    }
  );
  return data.member;
}

export interface LscCommissionerStaffMember extends LscMember {
  effective_role?: 'owner' | 'commissioner' | 'member';
}

export async function listLscCommissioners(leagueId: string): Promise<{
  ownerUid: string | null;
  commissioners: LscCommissionerStaffMember[];
}> {
  const data = await lscRequest<{
    success: boolean;
    owner_uid?: string | null;
    commissioners?: LscCommissionerStaffMember[];
  }>(leaguePath(leagueId, '/commissioners'));
  return {
    ownerUid: data.owner_uid ?? null,
    commissioners: data.commissioners ?? [],
  };
}

export async function assignLscCommissioner(
  leagueId: string,
  memberId: string
): Promise<LscMember> {
  const data = await lscRequest<{ success: boolean; member: LscMember }>(
    leaguePath(leagueId, '/commissioners'),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ member_id: memberId }),
    }
  );
  return data.member;
}

export async function revokeLscCommissioner(
  leagueId: string,
  memberId: string
): Promise<LscMember> {
  const data = await lscRequest<{ success: boolean; member: LscMember }>(
    leaguePath(leagueId, `/commissioners/${encodeURIComponent(memberId)}/revoke`),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    }
  );
  return data.member;
}

export async function createLscMember(
  leagueId: string,
  payload: Partial<LscMember>
): Promise<LscMember> {
  const data = await lscRequest<{ success: boolean; member: LscMember }>(
    leaguePath(leagueId, '/members'),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  );
  return data.member;
}

export async function updateLscMember(
  leagueId: string,
  memberId: string,
  payload: Partial<LscMember> & Record<string, unknown>
): Promise<LscMember> {
  const data = await lscRequest<{ success: boolean; member: LscMember }>(
    leaguePath(leagueId, `/members/${encodeURIComponent(memberId)}`),
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  );
  return data.member;
}

export async function getLscMemberTeamOptions(
  leagueId: string,
  params?: {
    current_member_id?: string;
    unclaimed_only?: boolean;
  }
): Promise<LscMemberTeamOptionsResult> {
  const search = new URLSearchParams();
  if (params?.current_member_id) {
    search.set('current_member_id', params.current_member_id);
  }
  if (params?.unclaimed_only) {
    search.set('unclaimed_only', 'true');
  }
  const query = search.toString();
  const suffix = query ? `/members/team-options?${query}` : '/members/team-options';
  return lscRequest<LscMemberTeamOptionsResult>(leaguePath(leagueId, suffix));
}

export async function getLscMemberProviderSync(
  leagueId: string
): Promise<LscMemberProviderSyncResult> {
  return lscRequest<LscMemberProviderSyncResult>(leaguePath(leagueId, '/members/provider-sync'));
}

export async function importLscCsv(
  leagueId: string,
  formData: FormData
): Promise<LscCsvImportResult> {
  return lscRequest<LscCsvImportResult>(leaguePath(leagueId, '/imports/csv'), {
    method: 'POST',
    body: formData,
  });
}

export async function listLscNeonsportzPublishedSeasons(
  leagueId: string,
  url: string
): Promise<{ success?: boolean; slug?: string; seasons: LscNeonsportzPublishedSeason[] }> {
  const search = new URLSearchParams({ url });
  return lscRequest(leaguePath(leagueId, `/imports/neonsportz/seasons?${search.toString()}`));
}

export async function previewLscNeonsportzUrl(
  leagueId: string,
  url: string,
  seasonLabel?: string,
  seasonIndex?: number | null
): Promise<LscNeonsportzUrlPreviewResult> {
  const payload: { url: string; season_label?: string; season_index?: number } = { url };
  if (typeof seasonIndex === 'number' && Number.isInteger(seasonIndex) && seasonIndex >= 0) {
    payload.season_index = seasonIndex;
  } else {
    const trimmedSeason = (seasonLabel || '').trim();
    if (trimmedSeason) {
      payload.season_label = trimmedSeason;
    }
  }
  return lscRequest<LscNeonsportzUrlPreviewResult>(
    leaguePath(leagueId, '/imports/neonsportz/preview'),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  );
}

export async function confirmLscNeonsportzUrl(
  leagueId: string,
  payload: {
    url: string;
    season_label?: string;
    season_index?: number;
    apply_matches?: boolean;
    confirm_season_rollover?: boolean;
    confirm_edition_rollover?: boolean;
    confirm_edition_undeclared?: boolean;
  }
): Promise<LscNeonsportzUrlConfirmResult> {
  return lscRequest<LscNeonsportzUrlConfirmResult>(
    leaguePath(leagueId, '/imports/neonsportz/confirm'),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  );
}

export async function getLscNeonsportzImportJob(
  leagueId: string,
  jobId: string
): Promise<LscNeonsportzUrlConfirmResult> {
  return lscRequest<LscNeonsportzUrlConfirmResult>(
    leaguePath(leagueId, `/imports/neonsportz/jobs/${encodeURIComponent(jobId)}`)
  );
}

async function sleepMs(ms: number): Promise<void> {
  await new Promise((resolve) => setTimeout(resolve, ms));
}

/** Confirm import and poll until the background job finishes (or return immediately on 201). */
export async function confirmLscNeonsportzUrlAndWait(
  leagueId: string,
  payload: {
    url: string;
    season_label?: string;
    season_index?: number;
    apply_matches?: boolean;
    confirm_season_rollover?: boolean;
    confirm_edition_rollover?: boolean;
    confirm_edition_undeclared?: boolean;
  },
  options?: { pollIntervalMs?: number; onStatus?: (status: string) => void }
): Promise<LscNeonsportzUrlConfirmResult> {
  const started = await confirmLscNeonsportzUrl(leagueId, payload);
  const jobId = started.job_id;
  const status = String(started.import_status || '');
  if (!jobId || status === 'succeeded' || started.snapshot_id) {
    return started;
  }
  const interval = options?.pollIntervalMs ?? 1500;
  for (;;) {
    await sleepMs(interval);
    const job = await getLscNeonsportzImportJob(leagueId, jobId);
    const nextStatus = String(job.import_status || '');
    options?.onStatus?.(nextStatus);
    if (nextStatus === 'succeeded') {
      return { ...job, success: true, job_id: jobId };
    }
    if (nextStatus === 'failed') {
      throw new LscApiError(
        job.message || job.error || 'Import failed.',
        500,
        typeof job.error === 'string' ? job.error : 'neonsportz_import_failed'
      );
    }
  }
}

export async function getLscSyncStatus(leagueId: string): Promise<LscSyncStatus> {
  const data = await lscRequest<{ success: boolean; sync_status: LscSyncStatus }>(
    leaguePath(leagueId, '/sync-status')
  );
  return data.sync_status;
}

export async function getLscAutoSyncRuns(
  leagueId: string,
  limit = 10
): Promise<{ league_id: string; runs: LscAutoSyncRun[]; limit: number }> {
  const clamped = Math.min(Math.max(1, limit), 50);
  const data = await lscRequest<{
    success: boolean;
    league_id: string;
    runs: LscAutoSyncRun[];
    limit: number;
  }>(leaguePath(leagueId, `/auto-sync/runs?limit=${clamped}`));
  return {
    league_id: data.league_id,
    runs: data.runs,
    limit: data.limit,
  };
}

export async function getLscLiveReadiness(leagueId: string): Promise<LscLiveReadinessReport> {
  const data = await lscRequest<{ success: boolean; live_readiness: LscLiveReadinessReport }>(
    leaguePath(leagueId, '/live-readiness')
  );
  return data.live_readiness;
}

export async function getLscLeagueReadiness(leagueId: string): Promise<LscLeagueReadiness> {
  const data = await lscRequest<{ success: boolean; readiness: LscLeagueReadiness }>(
    leaguePath(leagueId, '/readiness')
  );
  return data.readiness;
}

export async function getLscLeagueSetupProgress(
  leagueId: string
): Promise<LscLeagueSetupProgress> {
  const data = await lscRequest<{ success: boolean; setup_progress: LscLeagueSetupProgress }>(
    leaguePath(leagueId, '/setup-progress')
  );
  return data.setup_progress;
}

export async function patchLscLeagueSetupProgress(
  leagueId: string,
  body: { current_step_id?: LscLeagueSetupProgressStepId; mark_complete?: boolean }
): Promise<LscLeagueSetupProgress> {
  const data = await lscRequest<{ success: boolean; setup_progress: LscLeagueSetupProgress }>(
    leaguePath(leagueId, '/setup-progress'),
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }
  );
  return data.setup_progress;
}

export async function getLscLeagueAutomationAccess(
  leagueId: string
): Promise<LscLeagueAutomationAccess> {
  const data = await lscRequest<{ success: boolean; automation_access: LscLeagueAutomationAccess }>(
    leaguePath(leagueId, '/automation-access')
  );
  return data.automation_access;
}

export async function getLscAutomationActivation(
  leagueId: string
): Promise<LscAutomationActivationPayload> {
  const data = await lscRequest<{ success: boolean; activation: LscAutomationActivationPayload }>(
    leaguePath(leagueId, '/automation/activation')
  );
  return data.activation;
}

export async function getLscDetection(leagueId: string): Promise<LscDetectionPayload> {
  return lscRequest<LscDetectionPayload>(leaguePath(leagueId, '/detection'));
}

export async function patchLscDetection(
  leagueId: string,
  switchValue: LscDetectionSwitchValue
): Promise<LscDetectionPayload> {
  return lscRequest<LscDetectionPayload>(leaguePath(leagueId, '/detection'), {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ switch: switchValue }),
  });
}

export async function postLscAutomationActivation(
  leagueId: string
): Promise<LscAutomationActivationPayload> {
  const data = await lscRequest<{ success: boolean; activation: LscAutomationActivationPayload }>(
    leaguePath(leagueId, '/automation/activation'),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    }
  );
  return data.activation;
}

export async function getLscLiveSubscriptions(leagueId: string): Promise<LscLiveSubscription[]> {
  const data = await lscRequest<{ success: boolean; subscriptions: LscLiveSubscription[] }>(
    leaguePath(leagueId, '/live-subscriptions')
  );
  return data.subscriptions;
}

export async function syncLscLiveSubscriptions(
  leagueId: string,
  body: LscLiveSubscriptionSyncRequest = {}
): Promise<LscLiveSubscriptionSyncResult> {
  return lscRequest<LscLiveSubscriptionSyncResult>(
    leaguePath(leagueId, '/live-subscriptions/sync'),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }
  );
}

export async function patchLscScheduleContext(
  leagueId: string,
  payload: {
    active_stage_label?: string;
    active_season_label?: string;
  }
): Promise<{ active_season_label?: string | null; active_stage_label?: string | null }> {
  return lscRequest(
    leaguePath(leagueId, '/schedule-context'),
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  );
}

export async function getLscScheduleSource(leagueId: string): Promise<LscScheduleSourceStatus> {
  return lscRequest<LscScheduleSourceStatus>(leaguePath(leagueId, '/schedule-source'));
}

export async function putLscScheduleSource(
  leagueId: string,
  sourceUrl: string
): Promise<LscScheduleSourceStatus> {
  return lscRequest<LscScheduleSourceStatus>(leaguePath(leagueId, '/schedule-source'), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ source_url: sourceUrl }),
  });
}

export async function setLscAutoSyncSafeApply(
  leagueId: string,
  enabled: boolean
): Promise<LscScheduleSourceStatus> {
  return lscRequest<LscScheduleSourceStatus>(
    leaguePath(leagueId, '/schedule-source/auto-sync-safe-apply'),
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled }),
    }
  );
}

export async function deleteLscScheduleSource(
  leagueId: string
): Promise<{ success: boolean; schedule_source?: LscScheduleSource | null }> {
  return lscRequest(leaguePath(leagueId, '/schedule-source'), { method: 'DELETE' });
}

export async function syncLscScheduleSource(
  leagueId: string,
  options: { apply?: boolean; dry_run?: boolean } = {}
): Promise<LscScheduleSourceSyncResult> {
  return lscRequest<LscScheduleSourceSyncResult>(leaguePath(leagueId, '/schedule-source/sync'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(options),
  });
}

export async function repairLscScheduleSourceState(
  leagueId: string
): Promise<LscScheduleStaleRepairResult> {
  return lscRequest<LscScheduleStaleRepairResult>(leaguePath(leagueId, '/schedule-source/repair'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
}

export async function getScheduleRepairStatus(leagueId: string): Promise<ScheduleRepairStatus> {
  return lscRequest<ScheduleRepairStatus>(leaguePath(leagueId, '/schedule/repair/status'));
}

export async function previewScheduleRepair(
  leagueId: string,
  request: ScheduleRepairPreviewRequest = {}
): Promise<ScheduleRepairPreviewResponse> {
  return lscRequest<ScheduleRepairPreviewResponse>(leaguePath(leagueId, '/schedule/repair/preview'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });
}

export async function applyScheduleRepair(
  leagueId: string,
  request: ScheduleRepairApplyRequest
): Promise<ScheduleRepairApplyResponse> {
  return lscRequest<ScheduleRepairApplyResponse>(leaguePath(leagueId, '/schedule/repair/apply'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });
}

export async function promoteImportedLscMembers(
  leagueId: string,
  payload: {
    source?: string;
    snapshot_id?: string;
    dry_run?: boolean;
  }
): Promise<LscImportedMemberPromotionResult> {
  return lscRequest<LscImportedMemberPromotionResult>(
    leaguePath(leagueId, '/members/promote-imported'),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  );
}

export async function getMyLscMemberClaimSuggestions(
  leagueId: string
): Promise<LscMemberClaimSuggestionsResult> {
  return lscRequest<LscMemberClaimSuggestionsResult>(
    leaguePath(leagueId, '/member-claims/mine')
  );
}

export async function listMyLscClaimOpportunities(): Promise<LscMyClaimOpportunitiesResult> {
  return lscRequest<LscMyClaimOpportunitiesResult>('/api/lsc/my-claim-opportunities');
}

export async function submitLscMemberClaim(
  leagueId: string,
  memberId: string
): Promise<LscMemberClaimSubmitResult> {
  return lscRequest<LscMemberClaimSubmitResult>(
    leaguePath(leagueId, '/member-claims'),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ member_id: memberId }),
    }
  );
}

export async function listLscMemberClaims(
  leagueId: string,
  params: { include_decided?: boolean } = {}
): Promise<LscMemberClaimListResult> {
  const search = new URLSearchParams();
  if (params.include_decided === false) search.set('include_decided', 'false');
  const qs = search.toString();
  const path = leaguePath(leagueId, '/member-claims') + (qs ? `?${qs}` : '');
  return lscRequest<LscMemberClaimListResult>(path);
}

export async function approveLscMemberClaim(
  leagueId: string,
  claimId: string
): Promise<LscMemberClaimDecisionResult> {
  return lscRequest<LscMemberClaimDecisionResult>(
    leaguePath(leagueId, `/member-claims/${claimId}/approve`),
    { method: 'POST' }
  );
}

export async function rejectLscMemberClaim(
  leagueId: string,
  claimId: string,
  reason?: string
): Promise<LscMemberClaimDecisionResult> {
  return lscRequest<LscMemberClaimDecisionResult>(
    leaguePath(leagueId, `/member-claims/${claimId}/reject`),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    }
  );
}

export async function createLscLeagueInvite(
  leagueId: string,
  payload: { expires_in_days?: number; max_uses?: number | null } = {}
): Promise<LscLeagueInviteCreateResult> {
  return lscRequest<LscLeagueInviteCreateResult>(leaguePath(leagueId, '/invites'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export async function listLscLeagueInvites(
  leagueId: string
): Promise<LscLeagueInviteListResult> {
  return lscRequest<LscLeagueInviteListResult>(leaguePath(leagueId, '/invites'));
}

export async function revokeLscLeagueInvite(
  leagueId: string,
  inviteId: string,
  reason?: string
): Promise<LscLeagueInviteCreateResult> {
  return lscRequest<LscLeagueInviteCreateResult>(
    leaguePath(leagueId, `/invites/${encodeURIComponent(inviteId)}/revoke`),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    }
  );
}

export async function getLscInvitePreview(
  code: string
): Promise<LscLeagueInvitePreviewResult> {
  const trimmed = code.trim();
  return lscRequest<LscLeagueInvitePreviewResult>(
    `/api/lsc/invites/${encodeURIComponent(trimmed)}`
  );
}

export async function acceptLscInvite(
  code: string
): Promise<LscJoinRequestAcceptResult> {
  const trimmed = code.trim();
  return lscRequest<LscJoinRequestAcceptResult>(
    `/api/lsc/invites/${encodeURIComponent(trimmed)}/accept`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    }
  );
}

export async function listLscJoinRequests(
  leagueId: string,
  params: { status?: string; include_decided?: boolean } = {}
): Promise<LscJoinRequestListResult> {
  const search = new URLSearchParams();
  if (params.status) search.set('status', params.status);
  if (params.include_decided === false) search.set('include_decided', 'false');
  const qs = search.toString();
  const path = leaguePath(leagueId, '/join-requests') + (qs ? `?${qs}` : '');
  return lscRequest<LscJoinRequestListResult>(path);
}

export async function listMyLscJoinRequests(): Promise<LscMyJoinRequestListResult> {
  return lscRequest<LscMyJoinRequestListResult>('/api/lsc/my-join-requests');
}

export async function approveLscJoinRequest(
  leagueId: string,
  requestId: string,
  note?: string
): Promise<LscJoinRequestDecisionResult> {
  return lscRequest<LscJoinRequestDecisionResult>(
    leaguePath(leagueId, `/join-requests/${encodeURIComponent(requestId)}/approve`),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note }),
    }
  );
}

export async function rejectLscJoinRequest(
  leagueId: string,
  requestId: string,
  reason?: string
): Promise<LscJoinRequestDecisionResult> {
  return lscRequest<LscJoinRequestDecisionResult>(
    leaguePath(leagueId, `/join-requests/${encodeURIComponent(requestId)}/reject`),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    }
  );
}

export interface ListLscMatchesParams {
  stage_label?: string;
  season_label?: string;
  include_superseded?: boolean;
  scope?: 'active';
}

export async function listLscMatches(
  leagueId: string,
  params: ListLscMatchesParams = {}
): Promise<LscMatch[]> {
  const search = new URLSearchParams();
  if (params.stage_label) search.set('stage_label', params.stage_label);
  if (params.season_label) search.set('season_label', params.season_label);
  if (params.include_superseded) search.set('include_superseded', 'true');
  if (params.scope) search.set('scope', params.scope);
  const qs = search.toString();
  const path = withMemberPreview(leaguePath(leagueId, '/matches') + (qs ? `?${qs}` : ''));
  const data = await lscRequest<{ success: boolean; matches: LscMatch[] }>(path);
  return data.matches ?? [];
}

export async function listLscLiveLeagueGames(leagueId: string): Promise<LscLiveLeagueGame[]> {
  const data = await lscRequest<{
    success: boolean;
    league_id: string;
    live_games: LscLiveLeagueGame[];
  }>(leaguePath(leagueId, '/live-games'));
  return data.live_games ?? [];
}

export function mapLiveDashboardRowToLeagueGame(row: LscLiveDashboardRow): LscLiveLeagueGame {
  const resolvedMatchId = row.competition_match_id ?? row.likely_match_id;
  const matchId = String(resolvedMatchId ?? 'unknown').trim();
  const tier = String(row.dashboard_tier ?? '').trim().toLowerCase();
  const liveStreamKey = resolveLiveStreamKeyFromRow(row);
  return {
    liveStreamKey,
    live_game_id: row.stream_key ?? liveStreamKey,
    league_id: row.league_id ?? null,
    match_id: matchId,
    competition_match_id: resolvedMatchId ?? null,
    schedule_corrected: row.schedule_corrected ?? null,
    schedule_corrected_status_label: row.schedule_corrected_status_label ?? null,
    verification_status:
      tier === 'verified_live_game'
        ? 'verified_live_game'
        : tier === 'verified_match_side_pending'
          ? 'verified_match_side_pending'
        : tier === 'qualified_likely_game'
          ? 'qualified_likely_game'
          : tier || null,
    broadcaster_member_id: row.member_id ?? null,
    broadcaster_display_name: row.display_name ?? row.member_name ?? null,
    broadcaster_side: row.broadcaster_side ?? null,
    broadcaster_twitch_user_id: row.twitch_user_id ?? null,
    twitch_user_id: row.twitch_user_id ?? null,
    twitch_login: row.twitch_login ?? null,
    twitch_display_name: row.display_name ?? null,
    twitch_embed_url: row.embed_url ?? null,
    stream_started_at: row.stream_started_at ?? null,
    home_team: row.home_team ?? null,
    away_team: row.away_team ?? null,
    stage_label: row.stage_label ?? null,
    season_label: row.season_label ?? null,
    verification_confidence: row.live_truth_confidence ?? null,
    evidence_summary: row.evidence_summary ?? null,
    live_truth_status: row.live_truth_status ?? null,
    dashboard_tier: (row.dashboard_tier as LscLiveDashboardRow['dashboard_tier']) ?? null,
    canonical_state:
      typeof (row as { canonical_state?: unknown }).canonical_state === 'string'
        ? String((row as { canonical_state?: string }).canonical_state)
        : null,
    review_reasons: row.review_reasons ?? null,
    recommended_action: row.recommended_action ?? null,
    schedule_context_status: row.schedule_context_status ?? null,
    watch_url: row.watch_url ?? null,
    embed_url_ready: Boolean(row.twitch_login),
    matchup_display_mode:
      row.matchup_display_mode === 'neutral' || row.matchup_display_mode === 'verified'
        ? row.matchup_display_mode
        : null,
    matchup_label_suppressed: row.matchup_label_suppressed ?? null,
  };
}

export function resolveLiveStreamKeyFromRow(row: LscLiveDashboardRow): string {
  const streamKey = row.stream_key?.trim();
  if (streamKey) return streamKey;
  const twitchUserId = row.twitch_user_id?.trim();
  const streamId = (row as { stream_id?: string | null }).stream_id?.trim();
  if (twitchUserId && streamId) return `${twitchUserId}:${streamId}`;
  const login = row.twitch_login?.trim();
  const startedAt = row.stream_started_at?.trim();
  if (login && startedAt) return `${login}:${startedAt}`;
  const matchId = row.likely_match_id?.trim() ?? 'unknown';
  const memberId = row.member_id?.trim() ?? '';
  return `${matchId}:${memberId}:${login ?? 'unknown'}`;
}

export function resolveLiveStreamKey(game: LscLiveLeagueGame): string {
  const explicit = game.liveStreamKey?.trim();
  if (explicit) return explicit;
  const liveGameId = game.live_game_id?.trim();
  if (liveGameId) return liveGameId;
  const twitchUserId = game.twitch_user_id?.trim() ?? game.broadcaster_twitch_user_id?.trim();
  const streamId = (game as { stream_id?: string | null }).stream_id?.trim();
  if (twitchUserId && streamId) return `${twitchUserId}:${streamId}`;
  const login = game.twitch_login?.trim();
  const startedAt = game.stream_started_at?.trim();
  if (login && startedAt) return `${login}:${startedAt}`;
  return `${game.match_id}:${game.broadcaster_member_id ?? ''}:${login ?? 'unknown'}`;
}

export async function listLscLiveDashboard(leagueId: string): Promise<LscLiveDashboardResponse> {
  const data = await lscRequest<LscLiveDashboardResponse & { success: boolean }>(
    withMemberPreview(leaguePath(leagueId, '/live-dashboard'))
  );
  return {
    league_id: data.league_id,
    generated_at: data.generated_at,
    projection_enabled: data.projection_enabled,
    write_mode: data.write_mode,
    viewer_is_commissioner: data.viewer_is_commissioner,
    rows: data.rows ?? [],
    member_visible_rows: data.member_visible_rows ?? [],
    review_queue_rows: data.review_queue_rows ?? [],
    member_visible_count: data.member_visible_count ?? 0,
    review_queue_count: data.review_queue_count ?? 0,
    has_active_live_stream: data.has_active_live_stream ?? false,
    has_live_verification_in_progress: data.has_live_verification_in_progress ?? false,
    suppress_archive_fallback: data.suppress_archive_fallback ?? false,
  };
}

export async function getLscLiveTruthStatus(leagueId: string): Promise<LscLiveTruthStatusResponse> {
  const data = await lscRequest<LscLiveTruthStatusResponse & { success: boolean }>(
    leaguePath(leagueId, '/live-truth/status')
  );
  return {
    league_id: data.league_id,
    generated_at: data.generated_at,
    league_context: data.league_context,
    streams: data.streams ?? [],
  };
}

export async function runLscLiveTruthCheck(
  leagueId: string,
  payload: RunLscLiveTruthCheckPayload
): Promise<RunLscLiveTruthCheckResponse> {
  const body: RunLscLiveTruthCheckPayload = {
    twitch_user_id: payload.twitch_user_id,
  };
  if (payload.competition_match_id) {
    body.competition_match_id = payload.competition_match_id;
  }
  if (payload.stream_id) {
    body.stream_id = payload.stream_id;
  }
  return lscRequest<RunLscLiveTruthCheckResponse>(leaguePath(leagueId, '/live-truth/check'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function confirmLscBroadcasterSide(
  leagueId: string,
  payload: ConfirmLscBroadcasterSidePayload
): Promise<ConfirmLscBroadcasterSideResponse> {
  const body: ConfirmLscBroadcasterSidePayload = {
    broadcaster_side: payload.broadcaster_side,
  };
  if (payload.twitch_user_id) body.twitch_user_id = payload.twitch_user_id;
  if (payload.stream_id) body.stream_id = payload.stream_id;
  if (payload.competition_match_id) body.competition_match_id = payload.competition_match_id;
  if (payload.live_truth_check_id) body.live_truth_check_id = payload.live_truth_check_id;
  if (payload.needs_attention_item_key) {
    body.needs_attention_item_key = payload.needs_attention_item_key;
  }
  return lscRequest<ConfirmLscBroadcasterSideResponse>(
    leaguePath(leagueId, '/live-truth/confirm-side'),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }
  );
}

export interface SyncLscVodsPayload {
  dry_run?: boolean;
  max_per_member?: number;
  created_after?: string;
  member_id?: string;
}

export async function syncLscVods(
  leagueId: string,
  payload: SyncLscVodsPayload = {}
): Promise<LscSyncRunSummary> {
  return lscRequest<LscSyncRunSummary>(leaguePath(leagueId, '/vod-sync'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export async function getLscVodSyncReadiness(
  leagueId: string,
  params: { member_id?: string; resolve_helix?: boolean } = {}
): Promise<LscVodSyncReadiness & { manual_apply_enabled?: boolean }> {
  const search = new URLSearchParams();
  if (params.member_id) search.set('member_id', params.member_id);
  if (params.resolve_helix) search.set('resolve_helix', 'true');
  const qs = search.toString();
  const path = leaguePath(leagueId, '/vod-sync/readiness') + (qs ? `?${qs}` : '');
  const data = await lscRequest<{
    success: boolean;
    readiness: LscVodSyncReadiness;
    manual_apply_enabled?: boolean;
  }>(path);
  return {
    ...data.readiness,
    manual_apply_enabled: data.manual_apply_enabled ?? false,
  };
}

export interface GetLscCommissionerDigestParams {
  window_hours?: number;
  sample_limit?: number;
}

export async function getLscCommissionerDigest(
  leagueId: string,
  params: GetLscCommissionerDigestParams = {}
): Promise<LscCommissionerDigest> {
  const search = new URLSearchParams();
  search.set('window_hours', String(params.window_hours ?? 168));
  search.set('sample_limit', String(params.sample_limit ?? 5));
  const path = leaguePath(leagueId, '/commissioner-digest') + `?${search.toString()}`;
  return lscRequest<LscCommissionerDigest>(path);
}

export async function getLscPaidBetaSupportStatus(
  leagueId: string
): Promise<LscPaidBetaSupportStatus> {
  const path = leaguePath(leagueId, '/paid-beta-support-status');
  return lscRequest<LscPaidBetaSupportStatus>(path);
}

export interface ListLscVodCandidatesParams {
  candidate_status?: string;
  match_status?: string;
  limit?: number;
}

export async function listLscVodCandidates(
  leagueId: string,
  params: ListLscVodCandidatesParams = {}
): Promise<LscVodCandidate[]> {
  const search = new URLSearchParams();
  if (params.candidate_status) search.set('candidate_status', params.candidate_status);
  if (params.match_status) search.set('match_status', params.match_status);
  if (params.limit) search.set('limit', String(params.limit));
  const qs = search.toString();
  const path = leaguePath(leagueId, '/vod-candidates') + (qs ? `?${qs}` : '');
  const data = await lscRequest<{ success: boolean; candidates: LscVodCandidate[] }>(
    path
  );
  return data.candidates ?? [];
}

export type RefreshVodCandidateAvailabilityPayload = {
  dry_run?: boolean;
  max_candidates?: number;
  candidate_ids?: string[];
  confirm_rdl_canary_mark_unavailable?: boolean;
};

export type RefreshVodCandidateAvailabilityResult = {
  success: boolean;
  league_id: string;
  dry_run: boolean;
  checked_count: number;
  skipped_count: number;
  would_mark_expired: Array<Record<string, unknown>>;
  would_mark_unavailable: Array<Record<string, unknown>>;
  would_mark_available: Array<Record<string, unknown>>;
  unchanged: Array<Record<string, unknown>>;
  applied: Array<Record<string, unknown>>;
  errors: Array<Record<string, unknown>>;
};

export async function refreshLscVodCandidateAvailability(
  leagueId: string,
  payload: RefreshVodCandidateAvailabilityPayload = {}
): Promise<RefreshVodCandidateAvailabilityResult> {
  return lscRequest<RefreshVodCandidateAvailabilityResult>(
    leaguePath(leagueId, '/vod-candidates/availability-refresh'),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  );
}

export interface ListLscAutoVerifyEligibilityParams {
  limit?: number;
  sample_limit?: number;
  include_candidates?: boolean;
  min_matcher_confidence?: number;
  min_duration_seconds?: number;
  max_vod_age_days?: number;
}

export async function listLscAutoVerifyEligibility(
  leagueId: string,
  params: ListLscAutoVerifyEligibilityParams = {}
): Promise<LscAutoVerifyEligibilityReport> {
  const search = new URLSearchParams();
  search.set('limit', String(params.limit ?? 500));
  search.set('sample_limit', String(params.sample_limit ?? 5));
  search.set('include_candidates', params.include_candidates === false ? 'false' : 'true');
  if (params.min_matcher_confidence != null) {
    search.set('min_matcher_confidence', String(params.min_matcher_confidence));
  }
  if (params.min_duration_seconds != null) {
    search.set('min_duration_seconds', String(params.min_duration_seconds));
  }
  if (params.max_vod_age_days != null) {
    search.set('max_vod_age_days', String(params.max_vod_age_days));
  }
  const path = leaguePath(leagueId, '/auto-verify-eligibility') + `?${search.toString()}`;
  return lscRequest<LscAutoVerifyEligibilityReport>(path);
}

export interface ListLscVodHandoffSessionsParams {
  status?: string;
  twitch_user_id?: string;
  stream_id?: string;
  competition_match_id?: string;
  limit?: number;
}

export async function listLscVodHandoffSessions(
  leagueId: string,
  params: ListLscVodHandoffSessionsParams = {}
): Promise<LscVodHandoffSession[]> {
  const search = new URLSearchParams();
  if (params.status) search.set('status', params.status);
  if (params.twitch_user_id) search.set('twitch_user_id', params.twitch_user_id);
  if (params.stream_id) search.set('stream_id', params.stream_id);
  if (params.competition_match_id) search.set('competition_match_id', params.competition_match_id);
  if (params.limit) search.set('limit', String(params.limit));
  const qs = search.toString();
  const path = leaguePath(leagueId, '/vod-handoff/sessions') + (qs ? `?${qs}` : '');
  const data = await lscRequest<{ success: boolean; sessions: LscVodHandoffSession[] }>(path);
  return data.sessions ?? [];
}

export async function getLscVodCandidate(
  leagueId: string,
  candidateId: string
): Promise<LscVodCandidate> {
  const data = await lscRequest<{ success: boolean; candidate: LscVodCandidate }>(
    leaguePath(leagueId, `/vod-candidates/${encodeURIComponent(candidateId)}`)
  );
  return data.candidate;
}

export interface RunLscVodMatchPayload {
  dry_run?: boolean;
  candidate_id?: string;
  include_low_confidence?: boolean;
  created_after?: string;
  max_candidates?: number;
}

export async function runLscVodMatch(
  leagueId: string,
  payload: RunLscVodMatchPayload = {}
): Promise<LscVodMatchResult> {
  return lscRequest<LscVodMatchResult>(leaguePath(leagueId, '/vod-match'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export async function matchLscVodCandidate(
  leagueId: string,
  candidateId: string,
  payload: { dry_run?: boolean; include_low_confidence?: boolean } = {}
): Promise<LscVodMatchResult> {
  return lscRequest<LscVodMatchResult>(
    leaguePath(leagueId, `/vod-candidates/${encodeURIComponent(candidateId)}/match`),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  );
}

export async function listLscVodCandidateMatches(
  leagueId: string,
  candidateId: string,
  params: { include_low_confidence?: boolean; include_rejected?: boolean } = {}
): Promise<LscVodMatchSuggestion[]> {
  const search = new URLSearchParams();
  if (params.include_low_confidence) search.set('include_low_confidence', 'true');
  if (params.include_rejected) search.set('include_rejected', 'true');
  const qs = search.toString();
  const path =
    leaguePath(leagueId, `/vod-candidates/${encodeURIComponent(candidateId)}/matches`) +
    (qs ? `?${qs}` : '');
  const data = await lscRequest<{
    success: boolean;
    suggestions: LscVodMatchSuggestion[];
  }>(path);
  return data.suggestions ?? [];
}

// --- LSC.5 review / archive ---

export interface ListReviewQueueParams {
  review_status?: string;
  confidence_band?: string;
  member_id?: string;
  stage_label?: string;
  limit?: number;
}

export async function listReviewQueue(
  leagueId: string,
  params: ListReviewQueueParams = {}
): Promise<LscReviewQueueItem[]> {
  const search = new URLSearchParams();
  if (params.review_status) search.set('review_status', params.review_status);
  if (params.confidence_band) search.set('confidence_band', params.confidence_band);
  if (params.member_id) search.set('member_id', params.member_id);
  if (params.stage_label) search.set('stage_label', params.stage_label);
  if (params.limit) search.set('limit', String(params.limit));
  const qs = search.toString();
  const path = leaguePath(leagueId, '/review-queue') + (qs ? `?${qs}` : '');
  const data = await lscRequest<{ success: boolean; items: LscReviewQueueItem[] }>(
    path
  );
  return data.items ?? [];
}

export async function listLscNeedsAttention(
  leagueId: string,
  params: { limit?: number } = {}
): Promise<LscNeedsAttentionItem[]> {
  const search = new URLSearchParams();
  if (params.limit) search.set('limit', String(params.limit));
  const qs = search.toString();
  const path = leaguePath(leagueId, '/needs-attention') + (qs ? `?${qs}` : '');
  const data = await lscRequest<{ success: boolean; items: LscNeedsAttentionItem[] }>(path);
  return data.items ?? [];
}

export async function applyLscNeedsAttentionAction(
  leagueId: string,
  payload: { item_key: string; action: LscNeedsAttentionAction; note?: string }
): Promise<{ success: boolean; status?: string }> {
  return lscRequest<{ success: boolean; status?: string }>(
    leaguePath(leagueId, '/needs-attention/actions'),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  );
}

export async function listLscNeedsAttentionMatchOptions(
  leagueId: string,
  itemKey: string,
  params: {
    season?: string;
    week?: string;
    away_team?: string;
    home_team?: string;
    q?: string;
    limit?: number;
  } = {}
): Promise<LscNeedsAttentionMatchOptions> {
  const search = new URLSearchParams();
  if (params.season) search.set('season', params.season);
  if (params.week) search.set('week', params.week);
  if (params.away_team) search.set('away_team', params.away_team);
  if (params.home_team) search.set('home_team', params.home_team);
  if (params.q) search.set('q', params.q);
  if (params.limit) search.set('limit', String(params.limit));
  const qs = search.toString();
  const path =
    leaguePath(leagueId, `/needs-attention/${encodeURIComponent(itemKey)}/match-options`) +
    (qs ? `?${qs}` : '');
  return lscRequest<LscNeedsAttentionMatchOptions>(path);
}

export async function reassignLscNeedsAttentionMatchup(
  leagueId: string,
  itemKey: string,
  payload: { competition_match_id: string; approve?: boolean }
): Promise<LscNeedsAttentionReassignResult> {
  return lscRequest<LscNeedsAttentionReassignResult>(
    leaguePath(leagueId, `/needs-attention/${encodeURIComponent(itemKey)}/reassign`),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  );
}

export interface ReviewVodCandidatePayload {
  action: LscReviewAction;
  suggestion_id?: string;
  note?: string;
}

export async function reviewVodCandidate(
  leagueId: string,
  candidateId: string,
  payload: ReviewVodCandidatePayload
): Promise<LscReviewResponse> {
  return lscRequest<LscReviewResponse>(
    leaguePath(leagueId, `/vod-candidates/${encodeURIComponent(candidateId)}/review`),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  );
}

export async function ignoreLscVodCandidate(
  leagueId: string,
  candidateId: string,
  payload: LscIgnoreVodCandidatePayload
): Promise<LscIgnoreVodCandidateResponse> {
  return lscRequest<LscIgnoreVodCandidateResponse>(
    leaguePath(leagueId, `/vod-candidates/${encodeURIComponent(candidateId)}/ignore`),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  );
}

export interface VerifyLscVodCandidatePayload {
  force?: boolean;
}

export interface VerifyLscVodCandidateResponse {
  success: boolean;
  async?: boolean;
  dry_run?: boolean;
  candidate_id?: string;
  candidate?: LscVodCandidate;
  truth_status?: string;
  truth_verification_status?: string | null;
  truth_verification_progress_label?: string | null;
  truth_evidence?: Record<string, unknown>;
  truth_cost_estimate?: number;
  frames_used?: number;
  segments_used?: number;
  published?: boolean;
  task?: Record<string, unknown>;
}

export async function verifyLscVodCandidate(
  leagueId: string,
  candidateId: string,
  payload: VerifyLscVodCandidatePayload = {}
): Promise<VerifyLscVodCandidateResponse> {
  return lscRequest<VerifyLscVodCandidateResponse>(
    leaguePath(leagueId, `/vod-candidates/${encodeURIComponent(candidateId)}/verify`),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  );
}

export interface PublishVodCandidatePayload {
  suggestion_id?: string;
  visibility?: string;
  note?: string;
}

export async function publishVodCandidate(
  leagueId: string,
  candidateId: string,
  payload: PublishVodCandidatePayload = {}
): Promise<LscPublishResponse> {
  const { response, body } = await lscRequestRaw(
    leaguePath(leagueId, `/vod-candidates/${encodeURIComponent(candidateId)}/publish`),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  );

  if (response.status === 409) {
    const archiveId =
      body?.archive_id ||
      (body?.message?.includes('archive_id=')
        ? body.message.split('archive_id=').pop()?.trim()
        : undefined);
    throw new LscPublishConflictError(
      body?.message || 'This VOD is already published to another match.',
      archiveId
    );
  }

  if (!response.ok) {
    const message =
      body?.message ||
      (await readApiErrorMessage(response)) ||
      `Request failed (${response.status})`;
    throw new LscApiError(message, response.status, body?.error);
  }

  return body as LscPublishResponse;
}

export async function assignVodMatch(
  leagueId: string,
  candidateId: string,
  payload: { suggestion_id?: string; competition_match_id?: string; note?: string }
): Promise<LscReviewResponse> {
  return lscRequest<LscReviewResponse>(
    leaguePath(leagueId, `/vod-candidates/${encodeURIComponent(candidateId)}/assign-match`),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  );
}

export async function unassignVodMatch(
  leagueId: string,
  candidateId: string,
  payload?: { note?: string }
): Promise<LscReviewResponse & { review_event_id?: string; message?: string }> {
  return lscRequest(
    leaguePath(leagueId, `/vod-candidates/${encodeURIComponent(candidateId)}/unassign-match`),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload ?? {}),
    }
  );
}

export interface CreateLscIdentityOverridePayload {
  raw_tag: string;
  detected_side: string;
  member_id: string;
  note?: string;
}

export interface CreateLscIdentityOverrideResponse {
  success: boolean;
  candidate?: LscVodCandidate;
  review_event_id?: string;
  message?: string;
}

export async function createLscIdentityOverride(
  leagueId: string,
  candidateId: string,
  payload: CreateLscIdentityOverridePayload
): Promise<CreateLscIdentityOverrideResponse> {
  return lscRequest<CreateLscIdentityOverrideResponse>(
    leaguePath(
      leagueId,
      `/vod-candidates/${encodeURIComponent(candidateId)}/identity-overrides`
    ),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  );
}

export interface CreateLscEmptyHudHandoffAttestationPayload {
  note?: string;
}

export interface CreateLscEmptyHudHandoffAttestationResponse {
  success: boolean;
  candidate_id?: string;
  attestation?: Record<string, unknown>;
  attestation_count?: number;
  truth_empty_hud_handoff_attested_by_commissioner?: boolean;
  candidate?: LscVodCandidate;
  review_event_id?: string;
  message?: string;
}

export async function createLscEmptyHudHandoffAttestation(
  leagueId: string,
  candidateId: string,
  payload: CreateLscEmptyHudHandoffAttestationPayload = {}
): Promise<CreateLscEmptyHudHandoffAttestationResponse> {
  return lscRequest<CreateLscEmptyHudHandoffAttestationResponse>(
    leaguePath(
      leagueId,
      `/vod-candidates/${encodeURIComponent(candidateId)}/empty-hud-handoff-attestations`
    ),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  );
}

export interface ListLscArchiveParams {
  archive_status?: string;
  competition_match_id?: string;
  stage_label?: string;
  limit?: number;
  /** One card per competition match. Omit for the flat per-VOD list. */
  group?: 'match';
}

export async function listLscArchive(
  leagueId: string,
  params: ListLscArchiveParams = {}
): Promise<LscArchiveItem[]> {
  const search = new URLSearchParams();
  if (params.archive_status) search.set('archive_status', params.archive_status);
  if (params.competition_match_id) {
    search.set('competition_match_id', params.competition_match_id);
  }
  if (params.stage_label) search.set('stage_label', params.stage_label);
  if (params.limit) search.set('limit', String(params.limit));
  if (params.group) search.set('group', params.group);
  const qs = search.toString();
  const path = withMemberPreview(leaguePath(leagueId, '/archive') + (qs ? `?${qs}` : ''));
  const data = await lscRequest<{ success: boolean; items: LscArchiveItem[] }>(path);
  return data.items ?? [];
}

export async function getLscArchiveItem(
  leagueId: string,
  archiveId: string
): Promise<LscArchiveItem> {
  const data = await lscRequest<{ success: boolean; archive: LscArchiveItem }>(
    withMemberPreview(leaguePath(leagueId, `/archive/${encodeURIComponent(archiveId)}`))
  );
  return data.archive;
}

export async function unpublishLscArchiveItem(
  leagueId: string,
  archiveId: string,
  payload: { note?: string } = {}
): Promise<{
  success: boolean;
  archive?: LscArchiveItem;
  candidate?: LscVodCandidate;
  message?: string;
  review_event_id?: string;
}> {
  return lscRequest<{
    success: boolean;
    archive?: LscArchiveItem;
    candidate?: LscVodCandidate;
    message?: string;
    review_event_id?: string;
  }>(
    leaguePath(leagueId, `/archive/${encodeURIComponent(archiveId)}/unpublish`),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  );
}

export async function getLscBillingStatus(
  leagueId?: string
): Promise<LscBillingStatusResponse> {
  const qs = leagueId ? `?league_id=${encodeURIComponent(leagueId)}` : '';
  return lscRequest<LscBillingStatusResponse>(`/api/lsc/billing/status${qs}`);
}

export async function getLscCommissionerAccess(): Promise<LscCommissionerAccessResponse> {
  return lscRequest<LscCommissionerAccessResponse>('/api/lsc/account/commissioner-access');
}

export interface LscYouTubeConnection {
  success?: boolean;
  connected: boolean;
  youtube_channel_id?: string | null;
  youtube_channel_title?: string | null;
  connection_status: 'connected' | 'disconnected' | 'needs_reauth' | string;
  youtube_connected_at?: string | null;
}

export async function getYouTubeConnection(): Promise<LscYouTubeConnection> {
  return lscRequest<LscYouTubeConnection>('/api/auth/youtube/connection');
}

export async function disconnectYouTube(): Promise<{ success: boolean; youtube_connected: boolean }> {
  return lscRequest<{ success: boolean; youtube_connected: boolean }>(
    '/auth/youtube/disconnect',
    { method: 'POST' }
  );
}

export async function createLscCheckoutSession(
  billingCadence: Extract<LscBillingCadence, 'monthly' | 'annual'>,
  leagueId?: string
): Promise<LscCheckoutSessionResponse> {
  const payload: { billing_cadence: string; league_id?: string } = {
    billing_cadence: billingCadence,
  };
  const normalizedLeagueId = leagueId?.trim();
  if (normalizedLeagueId) {
    payload.league_id = normalizedLeagueId;
  }
  return lscRequest<LscCheckoutSessionResponse>('/api/lsc/billing/checkout', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export async function bindLscPaidSubscriptionToLeague(
  leagueId: string
): Promise<LscBindLeagueResponse> {
  return lscRequest<LscBindLeagueResponse>('/api/lsc/billing/bind-league', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ league_id: leagueId.trim() }),
  });
}

export async function createLscBillingPortalSession(
  leagueId?: string
): Promise<LscBillingPortalSessionResponse> {
  const body: Record<string, string> = {};
  const trimmedLeagueId = leagueId?.trim();
  if (trimmedLeagueId) {
    body.league_id = trimmedLeagueId;
  }
  return lscRequest<LscBillingPortalSessionResponse>('/api/lsc/billing/portal', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function createCfbSeason(
  leagueId: string,
  payload: CfbSeasonCreatePayload
): Promise<{ season: CfbDynastySeason }> {
  return lscRequest<{ success: boolean; season: CfbDynastySeason }>(
    leaguePath(leagueId, '/cfb/seasons'),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  );
}

export async function listCfbSeasons(
  leagueId: string
): Promise<{ seasons: CfbDynastySeason[]; count: number }> {
  return lscRequest<{ success: boolean; seasons: CfbDynastySeason[]; count: number }>(
    leaguePath(leagueId, '/cfb/seasons')
  );
}

export async function getCfbSeason(
  leagueId: string,
  seasonId: string
): Promise<{ season: CfbDynastySeason }> {
  return lscRequest<{ success: boolean; season: CfbDynastySeason }>(
    leaguePath(leagueId, `/cfb/seasons/${encodeURIComponent(seasonId)}`)
  );
}

export async function updateCfbSeason(
  leagueId: string,
  seasonId: string,
  payload: Partial<CfbSeasonCreatePayload>
): Promise<{ season: CfbDynastySeason }> {
  return lscRequest<{ success: boolean; season: CfbDynastySeason }>(
    leaguePath(leagueId, `/cfb/seasons/${encodeURIComponent(seasonId)}`),
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  );
}

export async function createCfbAssignment(
  leagueId: string,
  seasonId: string,
  payload: CfbAssignmentPayload
): Promise<{ assignment: CfbDynastyAssignment }> {
  return lscRequest<{ success: boolean; assignment: CfbDynastyAssignment }>(
    leaguePath(leagueId, `/cfb/seasons/${encodeURIComponent(seasonId)}/assignments`),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  );
}

export async function listCfbAssignments(
  leagueId: string,
  seasonId: string
): Promise<{ assignments: CfbDynastyAssignment[]; count: number }> {
  return lscRequest<{ success: boolean; assignments: CfbDynastyAssignment[]; count: number }>(
    leaguePath(leagueId, `/cfb/seasons/${encodeURIComponent(seasonId)}/assignments`)
  );
}

export async function updateCfbAssignment(
  leagueId: string,
  seasonId: string,
  assignmentId: string,
  payload: Partial<CfbAssignmentPayload>
): Promise<{ assignment: CfbDynastyAssignment }> {
  return lscRequest<{ success: boolean; assignment: CfbDynastyAssignment }>(
    leaguePath(
      leagueId,
      `/cfb/seasons/${encodeURIComponent(seasonId)}/assignments/${encodeURIComponent(assignmentId)}`
    ),
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  );
}

export async function deleteCfbAssignment(
  leagueId: string,
  seasonId: string,
  assignmentId: string
): Promise<{ assignment: CfbDynastyAssignment }> {
  return lscRequest<{ success: boolean; assignment: CfbDynastyAssignment }>(
    leaguePath(
      leagueId,
      `/cfb/seasons/${encodeURIComponent(seasonId)}/assignments/${encodeURIComponent(assignmentId)}`
    ),
    { method: 'DELETE' }
  );
}

export async function createCfbAlias(
  leagueId: string,
  seasonId: string,
  payload: CfbAliasPayload
): Promise<{ alias: CfbIdentityAlias }> {
  return lscRequest<{ success: boolean; alias: CfbIdentityAlias }>(
    leaguePath(leagueId, `/cfb/seasons/${encodeURIComponent(seasonId)}/aliases`),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  );
}

export async function listCfbAliases(
  leagueId: string,
  seasonId: string
): Promise<{ aliases: CfbIdentityAlias[]; count: number }> {
  return lscRequest<{ success: boolean; aliases: CfbIdentityAlias[]; count: number }>(
    leaguePath(leagueId, `/cfb/seasons/${encodeURIComponent(seasonId)}/aliases`)
  );
}

export async function updateCfbAlias(
  leagueId: string,
  seasonId: string,
  aliasId: string,
  payload: Partial<CfbAliasPayload>
): Promise<{ alias: CfbIdentityAlias }> {
  return lscRequest<{ success: boolean; alias: CfbIdentityAlias }>(
    leaguePath(
      leagueId,
      `/cfb/seasons/${encodeURIComponent(seasonId)}/aliases/${encodeURIComponent(aliasId)}`
    ),
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  );
}

export async function deleteCfbAlias(
  leagueId: string,
  seasonId: string,
  aliasId: string
): Promise<{ alias: CfbIdentityAlias }> {
  return lscRequest<{ success: boolean; alias: CfbIdentityAlias }>(
    leaguePath(
      leagueId,
      `/cfb/seasons/${encodeURIComponent(seasonId)}/aliases/${encodeURIComponent(aliasId)}`
    ),
    { method: 'DELETE' }
  );
}

export async function getCfbSetupCompleteness(
  leagueId: string,
  seasonId: string
): Promise<{ completeness: CfbSetupCompleteness }> {
  return lscRequest<{ success: boolean; completeness: CfbSetupCompleteness }>(
    leaguePath(leagueId, `/cfb/seasons/${encodeURIComponent(seasonId)}/setup-completeness`)
  );
}

export async function getCfbSchedule(
  leagueId: string,
  dynastySeasonId?: string
): Promise<import('./cfbScheduleTypes').CfbSchedulePayload> {
  const query = dynastySeasonId
    ? `?dynasty_season_id=${encodeURIComponent(dynastySeasonId)}`
    : '';
  return lscRequest<import('./cfbScheduleTypes').CfbSchedulePayload & { success: boolean }>(
    `${leaguePath(leagueId, '/cfb/schedule')}${query}`
  );
}

export async function createCfbScheduleGame(
  leagueId: string,
  body: import('./cfbScheduleTypes').CfbScheduleGameInput
): Promise<{ game: import('./cfbScheduleTypes').CfbScheduleGame }> {
  return lscRequest<{ success: boolean; game: import('./cfbScheduleTypes').CfbScheduleGame }>(
    leaguePath(leagueId, '/cfb/schedule/games'),
    { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }
  );
}

export async function updateCfbScheduleGame(
  leagueId: string,
  gameId: string,
  body: Partial<import('./cfbScheduleTypes').CfbScheduleGameInput>
): Promise<{ game: import('./cfbScheduleTypes').CfbScheduleGame }> {
  return lscRequest<{ success: boolean; game: import('./cfbScheduleTypes').CfbScheduleGame }>(
    leaguePath(leagueId, `/cfb/schedule/games/${encodeURIComponent(gameId)}`),
    { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }
  );
}

export async function deleteCfbScheduleGame(
  leagueId: string,
  gameId: string
): Promise<{ success: boolean }> {
  return lscRequest<{ success: boolean }>(
    leaguePath(leagueId, `/cfb/schedule/games/${encodeURIComponent(gameId)}`),
    { method: 'DELETE' }
  );
}

export async function approveCfbScheduleGame(
  leagueId: string,
  gameId: string
): Promise<{ game: import('./cfbScheduleTypes').CfbScheduleGame }> {
  return lscRequest<{ success: boolean; game: import('./cfbScheduleTypes').CfbScheduleGame }>(
    leaguePath(leagueId, `/cfb/schedule/games/${encodeURIComponent(gameId)}/approve`),
    { method: 'POST' }
  );
}

export async function lockCfbScheduleGame(
  leagueId: string,
  gameId: string
): Promise<{ game: import('./cfbScheduleTypes').CfbScheduleGame }> {
  return lscRequest<{ success: boolean; game: import('./cfbScheduleTypes').CfbScheduleGame }>(
    leaguePath(leagueId, `/cfb/schedule/games/${encodeURIComponent(gameId)}/lock`),
    { method: 'POST' }
  );
}

// --- LSC.CFB.7D vision screenshot schedule intake ---

export async function listCfbScheduleIntakeBatches(
  leagueId: string,
  dynastySeasonId?: string
): Promise<import('./cfbScheduleVisionTypes').CfbVisionIntakeBatch[]> {
  const query = dynastySeasonId
    ? `?dynasty_season_id=${encodeURIComponent(dynastySeasonId)}`
    : '';
  const data = await lscRequest<{
    success: boolean;
    batches: import('./cfbScheduleVisionTypes').CfbVisionIntakeBatch[];
  }>(`${leaguePath(leagueId, '/cfb/schedule/intake')}${query}`);
  return data.batches ?? [];
}

export async function createCfbScheduleIntakeBatch(
  leagueId: string,
  formData: FormData
): Promise<import('./cfbScheduleVisionTypes').CfbVisionIntakeBatchDetail> {
  return lscRequest<
    import('./cfbScheduleVisionTypes').CfbVisionIntakeBatchDetail & { success: boolean }
  >(leaguePath(leagueId, '/cfb/schedule/intake/screenshots'), {
    method: 'POST',
    body: formData,
  });
}

export async function createCfbScheduleIntakeVodBatch(
  leagueId: string,
  body: import('./cfbScheduleVisionTypes').CfbVodIntakeCreateBody
): Promise<import('./cfbScheduleVisionTypes').CfbVisionIntakeBatchDetail> {
  return lscRequest<
    import('./cfbScheduleVisionTypes').CfbVisionIntakeBatchDetail & { success: boolean }
  >(leaguePath(leagueId, '/cfb/schedule/intake/vod'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function getCfbScheduleIntakeBatch(
  leagueId: string,
  batchId: string
): Promise<import('./cfbScheduleVisionTypes').CfbVisionIntakeBatchDetail> {
  return lscRequest<
    import('./cfbScheduleVisionTypes').CfbVisionIntakeBatchDetail & { success: boolean }
  >(leaguePath(leagueId, `/cfb/schedule/intake/${encodeURIComponent(batchId)}`));
}

export async function updateCfbScheduleIntakeCandidate(
  leagueId: string,
  batchId: string,
  candidateId: string,
  body: import('./cfbScheduleVisionTypes').CfbVisionCandidatePatch
): Promise<{ candidate: import('./cfbScheduleVisionTypes').CfbVisionIntakeCandidate }> {
  return lscRequest<{
    success: boolean;
    candidate: import('./cfbScheduleVisionTypes').CfbVisionIntakeCandidate;
  }>(
    leaguePath(
      leagueId,
      `/cfb/schedule/intake/${encodeURIComponent(batchId)}/candidates/${encodeURIComponent(
        candidateId
      )}`
    ),
    { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }
  );
}

export async function importCfbScheduleIntakeCandidates(
  leagueId: string,
  batchId: string,
  candidateIds: string[]
): Promise<import('./cfbScheduleVisionTypes').CfbVisionImportResult> {
  return lscRequest<
    import('./cfbScheduleVisionTypes').CfbVisionImportResult & { success: boolean }
  >(leaguePath(leagueId, `/cfb/schedule/intake/${encodeURIComponent(batchId)}/import`), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ candidate_ids: candidateIds }),
  });
}

export async function cancelCfbScheduleIntakeBatch(
  leagueId: string,
  batchId: string
): Promise<{ batch: import('./cfbScheduleVisionTypes').CfbVisionIntakeBatch }> {
  return lscRequest<{
    success: boolean;
    batch: import('./cfbScheduleVisionTypes').CfbVisionIntakeBatch;
  }>(leaguePath(leagueId, `/cfb/schedule/intake/${encodeURIComponent(batchId)}/cancel`), {
    method: 'POST',
  });
}

/** Group archive items by competition_match_id for matches page indicators. */
export function groupArchivesByMatchId(
  items: LscArchiveItem[]
): Map<string, LscArchiveItem[]> {
  const map = new Map<string, LscArchiveItem[]>();
  for (const item of items) {
    const matchId = item.competition_match_id;
    if (!matchId) continue;
    const existing = map.get(matchId) ?? [];
    existing.push(item);
    map.set(matchId, existing);
  }
  return map;
}

export async function createLscSupportIssue(
  payload: LscSupportIssueCreateRequest
): Promise<LscSupportIssue> {
  const body = await lscRequest<LscSupportIssueCreateResponse>('/api/lsc/issues', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return body.issue;
}

export async function getMyLscSupportIssues(): Promise<LscSupportIssue[]> {
  const body = await lscRequest<LscSupportIssueMineResponse>('/api/lsc/issues/mine');
  return body.issues ?? [];
}

function buildAdminIssueListPath(filters?: LscAdminIssueListFilters): string {
  const params = new URLSearchParams();
  if (filters?.status) params.set('status', filters.status);
  if (filters?.severity) params.set('severity', filters.severity);
  if (filters?.category) params.set('category', filters.category);
  if (filters?.league_id?.trim()) params.set('league_id', filters.league_id.trim());
  const query = params.toString();
  return query ? `/api/lsc/admin/issues?${query}` : '/api/lsc/admin/issues';
}

export async function getLscAdminIssues(
  filters?: LscAdminIssueListFilters
): Promise<LscAdminSupportIssue[]> {
  const body = await lscRequest<LscAdminIssueListResponse>(buildAdminIssueListPath(filters));
  return body.issues ?? [];
}

export async function getLscAdminIssue(issueId: string): Promise<LscAdminSupportIssue> {
  const body = await lscRequest<LscAdminIssueDetailResponse>(
    `/api/lsc/admin/issues/${encodeURIComponent(issueId)}`
  );
  return body.issue;
}

export async function updateLscAdminIssue(
  issueId: string,
  patch: LscAdminIssueUpdateRequest
): Promise<LscAdminSupportIssue> {
  const body = await lscRequest<LscAdminIssueUpdateResponse>(
    `/api/lsc/admin/issues/${encodeURIComponent(issueId)}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    }
  );
  return body.issue;
}

export async function getLscAdminLeagueSummary(leagueId: string): Promise<LscAdminLeagueSummary> {
  const body = await lscRequest<LscAdminLeagueSummaryResponse>(
    `/api/lsc/admin/leagues/${encodeURIComponent(leagueId)}/summary`
  );
  return body.summary;
}

export async function getLscAdminAutopublishNightly(
  leagueId: string,
  day?: string
): Promise<{ day?: string; check_this?: boolean; text: string }> {
  const params = new URLSearchParams({ league_id: leagueId });
  if (day) params.set('day', day);
  return lscRequest(`/api/lsc/admin/autopublish-nightly?${params.toString()}`);
}

export async function getLscAdminHealth(): Promise<LscAdminHealth> {
  const body = await lscRequest<LscAdminHealthResponse>('/api/lsc/admin/health');
  return body;
}
